import React from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import FormHelperText from "@mui/material/FormHelperText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useLazyQuery, useQuery, useMutation } from "@apollo/client";
import { useFormik } from "formik";
import * as yup from "yup";
import NumberFormat from "react-number-format";
import { Link } from "react-router-dom";
import { EMPLOYEES } from "../../graphql/Employee";
import {
  CREATE_EXTERNAL_ORDER,
  FIND_EXTERNAL_ORDER_BY_REFERENCE,
} from "../../graphql/ExternalOrder";
import { CLIENTS } from "../../graphql/Client";
import MyBreadcrumbs from "../MyBreadcrumbs";
import GlobalContext from "../../utils/GlobalContext";
import dayjs from "dayjs";

const validationSchema = yup.object({
  employee_id: yup.string().required("Marketing harus diisi"),
  client_id: yup.string().required("Pelanggan harus diisi"),
  quarterly: yup.number().required("Cawu harus diisi"),
  total_sales: yup.number().required("Nominal Pembelanjaan harus diisi"),
  // tax: yup.number().required('Pajak harus diisi'),
  // mutation_date: yup.string().required("Tanggal Mutasi harus diisi"),
  type: yup.string().required("Tipe harus diisi"),
  company: yup.string().required("Perusahaan harus diisi"),
  additional: yup.boolean(),
  additional_fee_percentage: yup
    .number()
    .when("additional", {
      is: true,
      then: yup.number().min(1, "Persentase minimal 1%"),
    })
    .max(100, "Persentase minimal 100%"),
  is_custom_fee: yup.boolean(),
  custom_fee_percentage: yup
    .number()
    .when("is_custom_fee", {
      is: true,
      then: yup.number().min(1, "Persentase minimal 1%"),
    })
    .max(100, "Persentase minimal 100%"),
});

const SectionTitle = ({ children, action }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
    }}
  >
    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
      {children}
    </Typography>
    {action}
  </Box>
);

const ExternalOrderFormNew = () => {
  const { setSnack, setGlobalLoading } = React.useContext(GlobalContext);
  const [employees, setEmployees] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [invoices, setInvoices] = React.useState([
    {
      id: 1,
      noReference: {
        value: "",
        error: false,
      },
      nominal: {
        value: 0,
        error: false,
      },
      tax: {
        value: 0,
        error: false,
      },
      transactionAt: {
        value: "",
        error: false,
      },
      mutationAt: {
        value: "",
        error: false,
      },
      pphFee: {
        value: 0,
        error: false,
      },
      vaFee: {
        value: 0,
        error: false,
      },
      transactionFee: {
        value: 0,
        error: false,
      },
    },
  ]);

  /* FETCH DATA EMPLOYEES */
  const { data: dataEmployees } = useQuery(EMPLOYEES, {
    fetchPolicy: "network-only",
    onError: (e) => console.log("error getting selor:", e),
  });

  React.useEffect(() => {
    if (dataEmployees) {
      setEmployees(dataEmployees.employees);
    }
  }, [dataEmployees]);

  /* FETCH DATA CLIENTS */
  const { data: dataClients } = useQuery(CLIENTS, {
    fetchPolicy: "network-only",
    onError: (e) => console.log("error getting selor:", e),
  });

  React.useEffect(() => {
    if (dataClients) {
      setClients(dataClients.clients);
    }
  }, [dataClients]);

  const [findExternalOrderByReference] = useLazyQuery(
    FIND_EXTERNAL_ORDER_BY_REFERENCE,
    { fetchPolicy: "network-only" },
  );

  const checkNoReference = async (invoiceId, value) => {
    const reference = value?.trim();
    const invoice = invoices.find((item) => item.id === invoiceId);

    if (!reference || invoice?.noReference?.checkedValue === reference) return;

    setInvoices((currentInvoices) =>
      currentInvoices.map((item) =>
        item.id === invoiceId && item.noReference.value.trim() === reference
          ? {
              ...item,
              noReference: {
                ...item.noReference,
                checking: true,
                error: false,
                checkFailed: false,
                duplicateOrderId: null,
              },
            }
          : item,
      ),
    );

    try {
      const { data } = await findExternalOrderByReference({
        variables: { reference },
      });
      const existingOrder = data?.findExternalOrderByReference;

      setInvoices((currentInvoices) =>
        currentInvoices.map((item) =>
          item.id === invoiceId && item.noReference.value.trim() === reference
            ? {
                ...item,
                noReference: {
                  ...item.noReference,
                  checking: false,
                  checkFailed: false,
                  checkedValue: reference,
                  duplicateOrderId: existingOrder?.id ?? null,
                  error: Boolean(existingOrder),
                },
              }
            : item,
        ),
      );
    } catch {
      setInvoices((currentInvoices) =>
        currentInvoices.map((item) =>
          item.id === invoiceId && item.noReference.value.trim() === reference
            ? {
                ...item,
                noReference: {
                  ...item.noReference,
                  checking: false,
                  checkFailed: true,
                  checkedValue: null,
                  duplicateOrderId: null,
                  error: false,
                },
              }
            : item,
        ),
      );
    }
  };

  const [insertExternalOrder] = useMutation(CREATE_EXTERNAL_ORDER, {
    onCompleted: (d) => {
      // const data = externalOrder;
      // const baseData = baseExternalOrder;
      // setExternalOrder([...data, d.createExternalOrder]);
      // setBaseExternalOrder([...baseData, d.createExternalOrder]);

      setGlobalLoading(true);
      setSnack({
        variant: "success",
        message: "Data Nota berhasil disimpan",
        opened: true,
      });
      setGlobalLoading(false);
    },
    onError: (e) => {
      console.log("error insert external order: ", e);
      let message = "Data Data Nota gagal disimpan";

      setGlobalLoading(false);
      setSnack({
        variant: "error",
        message,
        opened: true,
      });
    },
  });

  const formik = useFormik({
    initialValues: {
      employee_id: "",
      client_id: "",
      client_name: "",
      quarterly: "",
      total_sales: 0,
      tax: 0,
      type: "",
      mutation_date: "",
      company: "",
      additional: false,
      additional_fee: 0,
      additional_fee_percentage: 0,
      is_custom_fee: false,
      custom_fee_percentage: 5,
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      const check = invoices.map((x) => {
        const reference = x.noReference.value?.trim();

        if (
          !reference ||
          (x.noReference.checkedValue !== reference && !x.noReference.checking)
        ) {
          x.noReference.error = true;
        }

        if (!x.nominal.value) {
          x.nominal.error = true;
        }

        if (!x.transactionAt.value) {
          x.transactionAt.error = true;
        }

        if (!x.mutationAt.value) {
          x.mutationAt.error = true;
        }

        return x;
      });

      const notValidate = check.filter(
        (y) =>
          y.noReference.error ||
          y.noReference.checking ||
          y.noReference.checkFailed ||
          y.nominal.error ||
          y.transactionAt.error ||
          y.mutationAt.error,
      );

      if (notValidate.length) {
        setInvoices([...check]);

        return;
      }

      const fee = calculateChangeAndFee();

      const externalOrderDetails = invoices.map((x) => ({
        no_reference: x.noReference?.value,
        nominal: x.nominal?.value,
        tax: x.tax?.value,
        transaction_at: x.transactionAt?.value,
        pph_fee: x.pphFee?.value,
        va_fee: x.vaFee?.value,
        transaction_fee: x.transactionFee?.value,
        mutation: calculateMutation(x),
        mutation_at: x.mutationAt?.value,
      }));

      const data = {
        client_id: values.client_id,
        employee_id: values.employee_id,
        quarterly: values.quarterly,
        type: values.type,
        total_payment_fee: calculateNominal(),
        total_sales: values.total_sales,
        tax: calculateTax(),
        admin_fee: fee.platform,
        tax_admin_fee: fee.expense,
        refund: fee.change,
        mutation_at: null,
        external_order_details: externalOrderDetails,
        company: values.company,
        additional: values?.additional,
        additional_fee: calculateChangeAndFee().additional_fee,
        total_pph_fee: calculatePphFee(),
        total_va_fee: calculateVaFee(),
        total_transaction_fee: calculateTransactionFee(),
        is_custom_fee: values?.is_custom_fee,
        custom_fee_percentage: values?.custom_fee_percentage,
        year: Number(
          dayjs(externalOrderDetails?.[0]?.transaction_at).format("YYYY"),
        ),
      };

      insertExternalOrder({
        variables: { data },
      });

      resetForm();
      setInvoices([
        {
          id: 1,
          noReference: {
            value: "",
            error: false,
          },
          nominal: {
            value: 0,
            error: false,
          },
          tax: {
            value: 0,
            error: false,
          },
          transactionAt: {
            value: "",
            error: false,
          },
          mutationAt: {
            value: "",
            error: false,
          },
          pphFee: {
            value: 0,
            error: false,
          },
          vaFee: {
            value: 0,
            error: false,
          },
          transactionFee: {
            value: 0,
            error: false,
          },
        },
      ]);
    },
  });

  const handleAddForm = () => {
    const oldProducts = invoices;
    setInvoices([
      ...oldProducts,
      {
        id: oldProducts.length + 1,
        noReference: {
          value: "",
          error: false,
        },
        nominal: {
          value: 0,
          error: false,
        },
        tax: {
          value: 0,
          error: false,
        },
        transactionAt: {
          value: "",
          error: false,
        },
        mutationAt: {
          value: "",
          error: false,
        },
        pphFee: {
          value: 0,
          error: false,
        },
        vaFee: {
          value: 0,
          error: false,
        },
        transactionFee: {
          value: 0,
          error: false,
        },
      },
    ]);
  };

  const handleRemoveForm = (i) => {
    let values = invoices;

    values.splice(i, 1);
    setInvoices([...values]);
  };

  const calculateNominal = () => {
    return invoices.reduce(
      (n, { nominal }) => Number(n) + Number(nominal.value),
      0,
    );
  };

  const calculateMutation = (invoice) =>
    Number(invoice.nominal.value || 0) -
    Number(invoice.tax.value || 0) -
    Number(invoice.pphFee.value || 0) -
    Number(invoice.vaFee.value || 0) -
    Number(invoice.transactionFee.value || 0);

  const calculateChangeAndFee = () => {
    const totalNominal = calculateNominal();
    const nominalTransaction = formik.values.total_sales;
    const tax = calculateTax();
    const pphFee = calculatePphFee();
    const vaFee = calculateVaFee();
    const transactionFee = calculateTransactionFee();
    const result = {
      change:
        totalNominal -
        nominalTransaction -
        tax -
        pphFee -
        vaFee -
        transactionFee,
    };
    const totalPercentage =
      (nominalTransaction /
        (totalNominal - tax - pphFee - vaFee - transactionFee)) *
      100;

    if (totalPercentage >= 40) {
      result.expense = 0;
      result.platform = 0;
      result.additional_fee = 0;
    } else {
      // result.platform = Math.round(result.change * 0.03 * 100) / 100;
      // result.expense = Math.round(result.change * 0.025 * 100) / 100;
      // CHANGE FORM 2.5% to 2% GOVERNMENT POLICY & SIPLAH
      // CHANGE 13-03-2023 FROM 0.03 TO 0.05
      const percentage = formik?.values?.is_custom_fee
        ? formik?.values?.custom_fee_percentage
        : 5;
      result.platform = Math.floor(result.change * (percentage / 100));

      let percentageExpense = 2;

      if (formik?.values?.is_custom_fee) {
        if (percentage <= 3) percentageExpense = 0;
        else percentageExpense = percentage - 3;
      }

      result.expense = Math.floor(result.change * (percentageExpense / 100));

      if (formik?.values?.additional) {
        result.additional_fee = Math.floor(
          result.change * (formik?.values?.additional_fee_percentage / 100),
        );
        result.change = result.change - result.platform - result.additional_fee;
      } else {
        result.additional_fee = 0;
        result.change = result.change - result.platform;
      }
    }

    return result;
  };

  const calculateTax = () => {
    return invoices.reduce((n, { tax }) => Number(n) + Number(tax.value), 0);
  };

  const calculatePphFee = () => {
    return invoices.reduce(
      (n, { pphFee }) => Number(n) + Number(pphFee.value),
      0,
    );
  };

  const calculateVaFee = () => {
    return invoices.reduce(
      (n, { vaFee }) => Number(n) + Number(vaFee.value),
      0,
    );
  };

  const calculateTransactionFee = () => {
    return invoices.reduce(
      (n, { transactionFee }) => Number(n) + Number(transactionFee.value),
      0,
    );
  };

  const renderInvoices = () => {
    return invoices.map((x, i) => {
      return (
        <Box key={x.id}>
          {i > 0 && <Divider sx={{ my: 2.5 }} />}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                autoComplete={`noReference${i}`}
                name={`noReference${i}`}
                fullWidth
                id={`noReference${i}`}
                label="No Referensi"
                placeholder="No Referensi"
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(e) => {
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    noReference: {
                      ...newArr[i].noReference,
                      value: e.target.value,
                      error: false,
                      checking: false,
                      checkFailed: false,
                      checkedValue: null,
                      duplicateOrderId: null,
                    },
                  };

                  setInvoices([...newArr]);
                }}
                onBlur={(e) => checkNoReference(x.id, e.target.value)}
                value={x.noReference.value}
                error={x.noReference.error || x.noReference.checkFailed}
                helperText={
                  x.noReference.duplicateOrderId ? (
                    <>
                      Referensi sudah digunakan.{" "}
                      <Link
                        to={`/admin/external_order/${x.noReference.duplicateOrderId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Buka order
                      </Link>
                    </>
                  ) : x.noReference.checking ? (
                    "Memeriksa nomor referensi..."
                  ) : x.noReference.checkFailed ? (
                    "Gagal memeriksa referensi. Keluar dari kolom untuk mencoba lagi."
                  ) : x.noReference.error ? (
                    x.noReference.value?.trim() ? (
                      "Keluar dari kolom untuk memeriksa nomor referensi."
                    ) : (
                      "Harus Diisi"
                    )
                  ) : (
                    " "
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                id={`transactionAt${i}`}
                name={`transactionAt${i}`}
                label="Tanggal Transaksi"
                type="date"
                value={x.transactionAt.value}
                error={x.transactionAt.error}
                helperText={x.transactionAt.error && "Harus Diisi"}
                onChange={(e) => {
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    transactionAt: { value: e.target.value, error: false },
                  };

                  setInvoices([...newArr]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                id={`mutationAt${i}`}
                name={`mutationAt${i}`}
                label="Tanggal Mutasi"
                type="date"
                value={x.mutationAt.value}
                error={x.mutationAt.error}
                helperText={x.mutationAt.error && "Harus Diisi"}
                onChange={(e) => {
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    mutationAt: { value: e.target.value, error: false },
                  };

                  setInvoices([...newArr]);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <NumberFormat
                key={`nominal${i}`}
                customInput={TextField}
                label="Nominal"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name={`nominal${i}`}
                id={`nominal${i}`}
                value={x.nominal.value}
                error={x.nominal.error}
                helperText={x.nominal.error && "Harus Diisi"}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    nominal: { value: floatValue || 0, error: false },
                  };

                  setInvoices([...newArr]);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <NumberFormat
                key={`tax${i}`}
                customInput={TextField}
                label="Pajak"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name={`tax${i}`}
                id={`tax${i}`}
                value={x.tax.value}
                error={x.tax.error}
                helperText={x.tax.error && "Harus Diisi"}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    tax: { value: floatValue || 0, error: false },
                  };

                  setInvoices([...newArr]);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <NumberFormat
                key={`pphFee${i}`}
                customInput={TextField}
                label="PPh SIPLAH"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name={`pphFee${i}`}
                id={`pphFee${i}`}
                value={x.pphFee.value}
                error={x.pphFee.error}
                helperText={x.pphFee.error && "Harus Diisi"}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    pphFee: { value: floatValue || 0, error: false },
                  };

                  setInvoices([...newArr]);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <NumberFormat
                key={`vaFee${i}`}
                customInput={TextField}
                label="Biaya VA"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name={`vaFee${i}`}
                id={`vaFee${i}`}
                value={x.vaFee.value}
                error={x.vaFee.error}
                helperText={x.vaFee.error && "Harus Diisi"}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    vaFee: { value: floatValue || 0, error: false },
                  };

                  setInvoices([...newArr]);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <NumberFormat
                key={`transactionFee${i}`}
                customInput={TextField}
                label="Biaya Transaksi / Beda Bank"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name={`transactionFee${i}`}
                id={`transactionFee${i}`}
                value={x.transactionFee.value}
                error={x.transactionFee.error}
                helperText={x.transactionFee.error && "Harus Diisi"}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  let newArr = [...invoices];
                  newArr[i] = {
                    ...newArr[i],
                    transactionFee: { value: floatValue || 0, error: false },
                  };

                  setInvoices([...newArr]);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <NumberFormat
                  key={`mutation${i}`}
                  customInput={TextField}
                  label="Mutasi"
                  variant="outlined"
                  isNumericString
                  thousandSeparator={"."}
                  decimalSeparator={","}
                  prefix="Rp. "
                  fullWidth
                  name={`mutation${i}`}
                  id={`mutation${i}`}
                  value={calculateMutation(x)}
                  disabled
                />
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  {i === invoices.length - 1 && (
                    <Tooltip title="Tambah Nota">
                      <IconButton
                        color="primary"
                        onClick={() => handleAddForm()}
                        size="small"
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {invoices.length > 1 && (
                    <Tooltip title="Hapus Nota">
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveForm(i)}
                        size="small"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    });
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          bgcolor: "background.paper",
          borderRadius: 1,
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <MyBreadcrumbs data={["Nota", "Tambah Data"]} />
        </Box>
      </Box>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{ maxWidth: 1200, mx: "auto", pb: 3 }}
      >
        {/* INFORMASI UMUM */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <SectionTitle>Informasi Umum</SectionTitle>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                disableClearable
                options={clients.map((option) => option.name)}
                id="client_id"
                name="client_id"
                label="Marketing"
                autoFocus
                value={formik.values.client_name}
                onInputChange={(_, v) => {
                  const client = clients.find((x) => x.name === v);

                  if (client) {
                    formik.setFieldValue("client_id", client.id);
                    formik.setFieldValue("client_name", client.name);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Nama Pelanggan" />
                )}
              />
              {formik.touched.client_id && Boolean(formik.errors.client_id) ? (
                <FormHelperText error>{formik.errors.client_id}</FormHelperText>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                error={
                  formik.touched.employee_id &&
                  Boolean(formik.errors.employee_id)
                }
              >
                <InputLabel id="employee_id">Marketing</InputLabel>
                <Select
                  labelId="employee_id"
                  id="employee_id"
                  name="employee_id"
                  label="Marketing"
                  value={formik.values.employee_id}
                  onChange={formik.handleChange}
                >
                  <MenuItem disabled selected value={""}>
                    Pilih
                  </MenuItem>
                  {employees?.map((x) => (
                    <MenuItem key={x.id} value={x.id}>
                      {x.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.employee_id &&
                Boolean(formik.errors.employee_id) ? (
                  <FormHelperText>{formik.errors.employee_id}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                error={formik.touched.company && Boolean(formik.errors.company)}
              >
                <InputLabel id="company">Perusahaan</InputLabel>
                <Select
                  labelId="company"
                  id="company"
                  name="company"
                  label="Perusahaan"
                  value={formik.values.company}
                  onChange={formik.handleChange}
                >
                  <MenuItem disabled selected value="">
                    Pilih
                  </MenuItem>
                  <MenuItem value="GJI">Global Jaya Indo</MenuItem>
                  <MenuItem value="GJA">Ganesha Jaya Artha</MenuItem>
                </Select>
                {formik.touched.company && Boolean(formik.errors.company) ? (
                  <FormHelperText>{formik.errors.company}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                error={
                  formik.touched.quarterly && Boolean(formik.errors.quarterly)
                }
              >
                <InputLabel id="quarterly">Cawu</InputLabel>
                <Select
                  labelId="quarterly"
                  id="quarterly"
                  name="quarterly"
                  label="Cawu"
                  value={formik.values.quarterly}
                  onChange={formik.handleChange}
                >
                  <MenuItem disabled selected value="">
                    Pilih
                  </MenuItem>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                </Select>
                {formik.touched.quarterly &&
                Boolean(formik.errors.quarterly) ? (
                  <FormHelperText>{formik.errors.quarterly}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                error={formik.touched.type && Boolean(formik.errors.type)}
              >
                <InputLabel id="type">Tipe</InputLabel>
                <Select
                  labelId="type"
                  id="type"
                  name="type"
                  label="Tipe"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                >
                  <MenuItem disabled selected value="">
                    Pilih
                  </MenuItem>
                  <MenuItem value="SIPLAH">SIPLAH</MenuItem>
                  <MenuItem value="BOSDA">BOSDA</MenuItem>
                  <MenuItem value="OFFLINE">OFFLINE</MenuItem>
                </Select>
                {formik.touched.type && Boolean(formik.errors.type) ? (
                  <FormHelperText>{formik.errors.type}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* DETAIL NOTA */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <SectionTitle
            action={
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleAddForm()}
              >
                Tambah Nota
              </Button>
            }
          >
            Detail Nota
          </SectionTitle>
          <Divider sx={{ mb: 3 }} />
          {renderInvoices()}
        </Paper>

        {/* RINGKASAN PEMBAYARAN */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <SectionTitle>Ringkasan Pembayaran</SectionTitle>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                key="totalNominal"
                customInput={TextField}
                label="Uang Masuk"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name="totalNominal"
                id="totalNominal"
                value={calculateNominal()}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                key="total_sales"
                customInput={TextField}
                label="Nominal Pembelanjaan"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                id="total_sales"
                name="total_sales"
                value={formik.values.total_sales}
                error={
                  formik.touched.total_sales &&
                  Boolean(formik.errors.total_sales)
                }
                helperText={
                  formik.touched.total_sales && formik.errors.total_sales
                }
                onValueChange={(values) => {
                  const { floatValue } = values;
                  formik.setFieldValue(
                    "total_sales",
                    floatValue ? floatValue : 0,
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                key="tax"
                customInput={TextField}
                label="Total Pajak"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                id="tax"
                name="tax"
                value={calculateTax()}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                key="pphFee"
                customInput={TextField}
                label="Total PPh SIPLAH"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                id="pphFee"
                name="pphFee"
                value={calculatePphFee()}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                key="vaFee"
                customInput={TextField}
                label="Total Biaya VA"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                id="vaFee"
                name="vaFee"
                value={calculateVaFee()}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                key="transactionFee"
                customInput={TextField}
                label="Total Biaya Transaksi / Beda Bank"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                id="transactionFee"
                name="transactionFee"
                value={calculateTransactionFee()}
                disabled
              />
            </Grid>
          </Grid>
        </Paper>

        {/* FEE & KEMBALIAN */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <SectionTitle>Fee &amp; Kembalian</SectionTitle>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="additional">Extra Fee</InputLabel>
                <Select
                  labelId="additional"
                  id="additional"
                  name="additional"
                  label="Extra Fee"
                  value={formik.values.additional}
                  onChange={formik.handleChange}
                >
                  <MenuItem disabled selected value="">
                    Pilih
                  </MenuItem>
                  <MenuItem value={true}>Ya</MenuItem>
                  <MenuItem value={false}>Tidak</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formik?.values?.additional && (
              <React.Fragment>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    autoComplete="additional_fee_percentage"
                    name="additional_fee_percentage"
                    fullWidth
                    id="additional_fee_percentage"
                    label="Fee Persentase"
                    placeholder="Fee Persentase"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={formik.handleChange}
                    type="number"
                    error={
                      formik.touched.additional &&
                      Boolean(formik.values.additional)
                    }
                    helperText={
                      formik.touched.additional_fee_percentage &&
                      formik.errors.additional_fee_percentage
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <NumberFormat
                    customInput={TextField}
                    label="Additional Fee"
                    variant="outlined"
                    isNumericString
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    prefix="Rp. "
                    fullWidth
                    name="additional_fee"
                    id="additional_fee"
                    value={calculateChangeAndFee().additional_fee}
                    disabled
                  />
                </Grid>
              </React.Fragment>
            )}

            {/* CUSTOM FREE PERCENTAGE
              - BY DEFAULT IS 5%
              - CAN CUSTOM
            */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="is_custom_fee">
                  Custom Platform Fee Percentage
                </InputLabel>
                <Select
                  labelId="is_custom_fee"
                  id="is_custom_fee"
                  name="is_custom_fee"
                  label="Custom Platform Fee Percentage"
                  value={formik.values.is_custom_fee}
                  onChange={formik.handleChange}
                >
                  <MenuItem disabled selected value="">
                    Pilih
                  </MenuItem>
                  <MenuItem value={true}>Ya</MenuItem>
                  <MenuItem value={false}>Tidak</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formik?.values?.is_custom_fee && (
              <React.Fragment>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    autoComplete="custom_fee_percentage"
                    name="custom_fee_percentage"
                    fullWidth
                    id="custom_fee_percentage"
                    label="Platform Fee Persentase"
                    placeholder="Platform Fee Persentase"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={formik.handleChange}
                    type="number"
                    error={
                      formik.touched.custom_fee_percentage &&
                      Boolean(formik.values.custom_fee_percentage)
                    }
                    helperText={
                      formik.touched.custom_fee_percentage &&
                      formik.errors.custom_fee_percentage
                    }
                    value={formik.values.custom_fee_percentage}
                  />
                </Grid>
              </React.Fragment>
            )}

            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                customInput={TextField}
                label="Platform Fee"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name="plaformFee"
                id="plaformFee"
                value={calculateChangeAndFee().platform}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                customInput={TextField}
                label="Expense Fee"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name="expenseFee"
                id="expenseFee"
                value={calculateChangeAndFee().expense}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NumberFormat
                customInput={TextField}
                label="Kembalian"
                variant="outlined"
                isNumericString
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="Rp. "
                fullWidth
                name="change"
                id="change"
                value={calculateChangeAndFee().change}
                disabled
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ACTION BAR */}
        <Paper
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            component={Link}
            to={"/admin/external_order/"}
            variant="outlined"
          >
            Kembali
          </Button>
          <Button variant="contained" type="submit">
            Simpan
          </Button>
        </Paper>
      </Box>
    </React.Fragment>
  );
};

export default ExternalOrderFormNew;
